import React, { useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as Icon from 'react-feather';

import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';
import FormInput from '../../../components/form/FormInput';
import Pagination from '../../../components/form/Pagination';
import ThreeSixtyCallDetailsModal from './ThreeSixtyCallDetailsModal';

import {
  threeSixtyReportExport,
  threeSixtyReportGet
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import { checkModulePermission, formatCellValue } from '../../../utils/common';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import useReportDownload from '../../../hooks/useReportDownload';

const EXPORT_NAME = '360 Details';

const ThreeSixtyDetailsReport = () => {
  const dispatch = useDispatch();
  const userRolePermission = useUserRolePermission();
  const { isDownloading, downloadFile } = useReportDownload();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  // Number the current grid was built for, frozen at submit time. Paging reuses this
  // instead of the live input, so editing the field without searching can't send a
  // different (or empty) number on the next page click.
  const [appliedNumber, setAppliedNumber] = useState('');
  // uuid of the call whose details popup is open.
  const [detailsUuid, setDetailsUuid] = useState(null);

  const initialValues = {
    page: 1,
    limit: 10,
    destination_number: ''
  };

  const validationSchema = Yup.object({
    destination_number: Yup.string()
      .trim()
      .matches(/^\+?\d+$/, 'Digits only, with an optional leading +')
      .required('Destination number is required')
  });

  const fetchReportData = async (overrides = {}) => {
    const payload = {
      page: values.page,
      limit: values.limit,
      destination_number: appliedNumber,
      ...overrides
    };
    const destinationNumber = (payload.destination_number || '').trim();
    if (!destinationNumber) return;

    setIsLoading(true);
    setIsFilterOpen(false);
    setAppliedNumber(destinationNumber);
    try {
      const response = await dispatch(
        threeSixtyReportGet({ ...payload, destination_number: destinationNumber })
      ).unwrap();
      setReportData(response || null);
    } catch (error) {
      setReportData(null);
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Exports every call of the searched number (all pages), built by the backend like the
  // other reports' downloads.
  const handleExportExcel = () => {
    if (!appliedNumber) return;
    downloadFile({
      name: EXPORT_NAME,
      count: reportData?.total,
      fileName: `360-details-report-${appliedNumber}.xlsx`,
      request: (onDownloadProgress) =>
        dispatch(
          threeSixtyReportExport({ destination_number: appliedNumber, onDownloadProgress })
        ).unwrap()
    });
  };

  const onSubmit = (formValues, { setFieldValue }) => {
    setFieldValue('page', 1);
    fetchReportData({ destination_number: formValues.destination_number, page: 1 });
  };

  const { values, handleBlur, setFieldValue, handleSubmit, handleChange, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  return (
    <div>
      <AdminHeader
        title="360 Details Reports"
        isSearch={true}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isExportDisabled={
          !checkModulePermission('is_export', '360 Details Reports', userRolePermission)
        }
        isManualExport
        isExporting={isDownloading(EXPORT_NAME)}
        isExport={reportData?.data?.length > 0}
        onExportClick={() => handleExportExcel()}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="4" className="flex-row">
                <FormInput
                  name="destination_number"
                  label="Destination Number"
                  placeholder="Enter destination number"
                  value={values.destination_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.data?.length > 0 ? (
        <Card className="p-0 position-relative m-0">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 370px)' : 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {reportData?.header?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                  <th className="text-center" style={{ width: '90px' }}>
                    Info
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportData?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {reportData?.header?.map((value, idx) => {
                        return <td key={idx}>{formatCellValue(val[value?.name])}</td>;
                      })}
                      <td>
                        <div className="d-flex justify-content-center align-items-center">
                          {val?.uuid ? (
                            <Icon.Info
                              size={16}
                              className="cursor-pointer text-primary"
                              onClick={() => setDetailsUuid(val.uuid)}
                            />
                          ) : (
                            '-'
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={reportData?.total}
              limit={reportData?.limit || values.limit}
              page={reportData?.page || values.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
                fetchReportData({ limit: val, page: 1 });
              }}
              onPageClick={(val) => {
                setFieldValue('page', val);
                fetchReportData({ page: val });
              }}
              lastPageClick={(val) => {
                setFieldValue('page', val);
                fetchReportData({ page: val });
              }}
              firstPageClick={(val) => {
                setFieldValue('page', val);
                fetchReportData({ page: val });
              }}
              onRefreshClick={() => fetchReportData()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          {appliedNumber ? 'No data available' : 'Enter a destination number to build the 360 view'}
        </Card>
      )}
      {detailsUuid && (
        <ThreeSixtyCallDetailsModal
          isOpen={!!detailsUuid}
          handleCancel={() => setDetailsUuid(null)}
          uuid={detailsUuid}
        />
      )}
    </div>
  );
};

export default ThreeSixtyDetailsReport;
